import { useMemo, useState } from 'react';
import { useFinance } from '@/hooks/useFinance';
import {
  ACCOUNT_TYPES,
  accountTypeMeta,
  formatDate,
  formatMoney,
  type FinanceAccountType,
} from '@/lib/finance';
import { FinanceSection, FinanceEmpty, FinanceModal, Field, inputClass, FinanceStatCard } from '../components/FinanceUI';

export default function FinanceTresoreriePage() {
  const finance = useFinance();
  const { currency, accounts, accountBalances, totalCash, transactions, expenses, invoices, suppliers } = finance;

  const [accountOpen, setAccountOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [type, setType] = useState<FinanceAccountType>('cash');
  const [opening, setOpening] = useState('');

  const [fromId, setFromId] = useState('');
  const [toId, setToId] = useState('');
  const [transferAmount, setTransferAmount] = useState('');
  const [transferNote, setTransferNote] = useState('');

  const totalIn = transactions.filter((t) => t.direction === 'in').reduce((s, t) => s + Number(t.amount), 0);
  const totalOut = transactions.filter((t) => t.direction === 'out').reduce((s, t) => s + Number(t.amount), 0) + expenses.reduce((s, e) => s + Number(e.amount), 0);

  // Échéances à venir (factures non soldées, dettes fournisseurs, récurrences).
  const upcoming = useMemo(() => {
    const rows: { label: string; amount: number; date: string | null; icon: string }[] = [];
    invoices.filter((i) => i.status !== 'paid' && i.status !== 'cancelled' && i.due_date).forEach((i) => {
      rows.push({ label: `Facture ${i.number} — ${i.customer_name || 'Client'}`, amount: Math.max(0, i.total - i.amount_paid), date: i.due_date, icon: 'ri-file-text-line' });
    });
    suppliers.forEach((s) => {
      const bal = finance.supplierBalance(s.id);
      if (bal > 0) rows.push({ label: `Fournisseur ${s.name}`, amount: bal, date: null, icon: 'ri-truck-line' });
    });
    finance.recurring.filter((r) => r.is_active && r.next_due_date).forEach((r) => {
      rows.push({ label: r.description || r.category, amount: Number(r.amount), date: r.next_due_date, icon: 'ri-loop-left-line' });
    });
    return rows.sort((a, b) => {
      if (!a.date) return 1;
      if (!b.date) return -1;
      return a.date.localeCompare(b.date);
    }).slice(0, 10);
  }, [invoices, suppliers, finance]);

  const submitAccount = async () => {
    if (!name.trim()) { setMsg('Nom requis'); return; }
    setBusy(true);
    try {
      await finance.createAccount({ name: name.trim(), account_type: type, opening_balance: parseFloat(opening) || 0 });
      setName(''); setOpening(''); setType('cash');
      setAccountOpen(false);
    } catch (err) {
      setMsg(err instanceof Error ? err.message : 'Erreur');
    } finally { setBusy(false); }
  };

  const submitTransfer = async () => {
    const value = parseFloat(transferAmount);
    if (!fromId || !toId || fromId === toId || !value || value <= 0) { setMsg('Vérifiez les comptes et le montant'); return; }
    setBusy(true);
    try {
      await finance.transfer(Number(fromId), Number(toId), value, transferNote || undefined);
      setTransferAmount(''); setTransferNote(''); setFromId(''); setToId('');
      setTransferOpen(false);
    } catch (err) {
      setMsg(err instanceof Error ? err.message : 'Erreur');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      {msg && <div className="px-4 py-2.5 rounded-lg bg-secondary-50 text-secondary-800 text-sm">{msg}</div>}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <FinanceStatCard label="Trésorerie disponible" value={formatMoney(totalCash, currency)} icon="ri-wallet-3-line" tone="primary" />
        <FinanceStatCard label="Encaissements" value={formatMoney(totalIn, currency)} icon="ri-arrow-down-circle-line" tone="secondary" />
        <FinanceStatCard label="Décaissements" value={formatMoney(totalOut, currency)} icon="ri-arrow-up-circle-line" tone="accent" />
        <FinanceStatCard label="Trésorerie prévue" value={formatMoney(upcoming.reduce((s, u) => s + u.amount, 0) ? totalCash + upcoming.reduce((s, u) => s + u.amount, 0) : totalCash, currency)} icon="ri-funds-line" tone="secondary" hint="Solde + échéances à venir" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => { setName(''); setOpening(''); setAccountOpen(true); }} className="flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer">
          <i className="ri-add-line"></i>Nouveau compte
        </button>
        {accounts.length >= 2 && (
          <button onClick={() => setTransferOpen(true)} className="flex items-center gap-1.5 px-3.5 py-2 bg-background-50 border border-background-200/70 text-foreground-700 rounded-full text-sm font-medium whitespace-nowrap hover:bg-background-100 transition-colors cursor-pointer">
            <i className="ri-arrow-left-right-line"></i>Transfert
          </button>
        )}
      </div>

      <FinanceSection title="Mes comptes" icon="ri-bank-line">
        {accounts.length === 0 ? (
          <FinanceEmpty icon="ri-bank-line" title="Aucun compte" hint="Créez votre caisse, un compte bancaire ou un portefeuille mobile." />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {accounts.map((a) => {
              const meta = accountTypeMeta(a.account_type);
              const bal = accountBalances[a.id] || 0;
              return (
                <div key={a.id} className="bg-background-50 border border-background-200/70 rounded-lg p-4 flex flex-col gap-2">
                  <div className="flex items-center gap-2.5">
                    <span className="w-9 h-9 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center flex-shrink-0">
                      <i className={`${meta.icon} text-base`}></i>
                    </span>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-foreground-900 truncate">{a.name}</div>
                      <div className="text-xs text-foreground-400">{meta.label}</div>
                    </div>
                    <button onClick={() => finance.deleteAccount(a.id)} className="ml-auto w-7 h-7 flex items-center justify-center rounded-md text-foreground-300 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer" title="Supprimer">
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  </div>
                  <div className={`text-lg font-bold font-heading ${bal >= 0 ? 'text-primary-700' : 'text-red-600'}`}>{formatMoney(bal, currency)}</div>
                </div>
              );
            })}
          </div>
        )}
      </FinanceSection>

      <FinanceSection title="Échéances à venir" icon="ri-calendar-check-line">
        {upcoming.length === 0 ? (
          <p className="text-sm text-foreground-400">Aucune échéance à venir enregistrée.</p>
        ) : (
          <div className="divide-y divide-background-200/70">
            {upcoming.map((u, i) => (
              <div key={`${u.label}-${i}`} className="flex items-center gap-3 py-2.5">
                <span className="w-8 h-8 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
                  <i className={`${u.icon} text-sm`}></i>
                </span>
                <span className="flex-1 min-w-0 text-sm text-foreground-800 truncate">{u.label}</span>
                <span className="text-xs text-foreground-400 whitespace-nowrap">{u.date ? formatDate(u.date) : '—'}</span>
                <span className="text-sm font-semibold text-foreground-800 whitespace-nowrap">{formatMoney(u.amount, currency)}</span>
              </div>
            ))}
          </div>
        )}
      </FinanceSection>

      {accountOpen && (
        <FinanceModal title="Nouveau compte" onClose={() => setAccountOpen(false)}>
          <Field label="Nom du compte *">
            <input value={name} onChange={(e) => setName(e.target.value)} className={inputClass} placeholder="Ex : Caisse boutique" />
          </Field>
          <Field label="Type">
            <select value={type} onChange={(e) => setType(e.target.value as FinanceAccountType)} className={inputClass}>
              {ACCOUNT_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </Field>
          <Field label="Solde d'ouverture">
            <input type="number" step="0.01" value={opening} onChange={(e) => setOpening(e.target.value)} className={inputClass} placeholder="0.00" />
          </Field>
          <button onClick={submitAccount} disabled={busy} className="w-full mt-2 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer whitespace-nowrap">
            {busy ? <i className="ri-loader-4-line animate-spin"></i> : 'Créer le compte'}
          </button>
        </FinanceModal>
      )}

      {transferOpen && (
        <FinanceModal title="Transfert entre comptes" onClose={() => setTransferOpen(false)}>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Depuis">
              <select value={fromId} onChange={(e) => setFromId(e.target.value)} className={inputClass}>
                <option value="">—</option>
                {accounts.map((a) => <option key={a.id} value={String(a.id)}>{a.name}</option>)}
              </select>
            </Field>
            <Field label="Vers">
              <select value={toId} onChange={(e) => setToId(e.target.value)} className={inputClass}>
                <option value="">—</option>
                {accounts.map((a) => <option key={a.id} value={String(a.id)}>{a.name}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Montant *">
            <input type="number" min="0" step="0.01" value={transferAmount} onChange={(e) => setTransferAmount(e.target.value)} className={inputClass} placeholder="0.00" />
          </Field>
          <Field label="Note">
            <input value={transferNote} onChange={(e) => setTransferNote(e.target.value)} className={inputClass} placeholder="Ex : Retrait pour dépôt bancaire" />
          </Field>
          <button onClick={submitTransfer} disabled={busy} className="w-full mt-2 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer whitespace-nowrap">
            {busy ? <i className="ri-loader-4-line animate-spin"></i> : 'Effectuer le transfert'}
          </button>
          <p className="text-[11px] text-foreground-400 mt-2 flex items-start gap-1">
            <i className="ri-information-line mt-0.5"></i>Un transfert crée deux écritures équilibrées et n&apos;affecte ni les revenus ni les dépenses.
          </p>
        </FinanceModal>
      )}
    </div>
  );
}